import { getTagTypes } from "@/constants/tag-type";
import { Fingers } from "../constants/biometric-constant";
import { STANDARD_NUMBER_OF_BITS } from "../constants/credential-constants";
import { CREDENTIAL_RECOGNITION_TYPE, CREDENTIAL_STATES } from "../constants/system-constants";
import tagStatus from "../constants/tag-status";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { Tag } from "../models/Tag";
import tagStatusOptions from "../models/TagStatusOptions";

export const getStatus = (statusId, translate): {} => {
    let status: {} = {};

    // Suspend
    if (statusId === tagStatusOptions(translate)[1].value) {
        status = { suspend: 1, blacklist: 0, accessOverride: 0, guardTour: 0, specialEv: 0 };
    }
    // Suspend with alarm
    else if (statusId === tagStatusOptions(translate)[2].value) {
        status = { blacklist: 1, suspend: 0, accessOverride: 0, guardTour: 0, specialEv: 0 };
    }
    // Access Override
    else if (statusId === tagStatusOptions(translate)[3].value) {
        status = { accessOverride: 1, suspend: 0, blacklist: 0, guardTour: 0, specialEv: 0 };
    }
    // Special Event 1
    else if (statusId === tagStatusOptions(translate)[4].value) {
        status = { specialEv: 1, accessOverride: 0, suspend: 0, blacklist: 0, guardTour: 0 };
    }
    // Special Event 2
    else if (statusId === tagStatusOptions(translate)[5].value) {
        status = { specialEv: 2, accessOverride: 0, suspend: 0, blacklist: 0, guardTour: 0 };
    }
    // Normal
    else {
        status = { guardTour: 0, suspend: 0, accessOverride: 0, blacklist: 0, specialEv: 0 };
    }

    return status;
}

export const getStatusId = (status: string, translate: Translator): string => {
    const statusOption = tagStatusOptions(translate).find((statusOption) => statusOption.label === status);
    return statusOption ? statusOption.value : '';
};

export const getStatusName = (statusId: number, translate: Translator) => {
    let statusName: string = '';
    if (statusId) {
        statusName = tagStatusOptions(translate)[statusId - 1].label;
    }

    return statusName;
}

export const buildCardData = (card: Tag, translate: Translator) : Tag => {
    return {
        id: card.id,
        masterId: card.masterId,
        typeId: card.typeId,
        code: card.code,
        codeUntruncated: card.codeUntruncated ? card.codeUntruncated : card.code,
        startDate: card.startDate,
        startTime: card.startTime,
        expiryDate: card.expiryDate,
        expiryTime: card.expiryTime,
        maIdentifier: card.maIdentifier,
        isCodeModified:card.isCodeModified,
        truncationFormatId: card.numberOfBits === 26 ? '12' : card.truncationFormatId,
        numberOfBits: card.numberOfBits ?? STANDARD_NUMBER_OF_BITS,
        tagByteReversed: card.tagByteReversed,
        tagOss: card.tagOss && card.typeId === getTagTypes().General.value ? card.tagOss : "0", // Safe-guard to ensure only General cards can have OSS values, and that non-General cards don't have OSS values.
        ...getStatus(card.statusId, translate)
    };
}

export const generateNewRandomPAC = (cardInfo: Tag, translate: Translator) => {
    const PAC_TAG_TYPE_ID: string = getTagTypes().Personal_Access_Code.value;
    const MAX_TAG_CODE: number = 99999;
    const code: string = (Math.floor(Math.random() * MAX_TAG_CODE) + 1).toString();
    const tag: Tag = {
        ...cardInfo,
        code: code,
        typeId: PAC_TAG_TYPE_ID,
        type: translate.byKey(TranslationKeys.personalAccessCode),
        statusId: tagStatus.Normal.value,
    }

   return tag;
}

export const translateCredentialType = (keyToTranslate: string, translator: Translator) => {
    let translatedValue = keyToTranslate;
    switch(keyToTranslate) {
        case CREDENTIAL_RECOGNITION_TYPE.MOBILE:
            translatedValue = translator.byKey(TranslationKeys.mobile);
        break;
        case CREDENTIAL_RECOGNITION_TYPE.CARD:
            translatedValue = translator.byKey(TranslationKeys.card);
        break;
        case CREDENTIAL_RECOGNITION_TYPE.QR_CODE:
            translatedValue = translator.byKey(TranslationKeys.qrCodeDescription);
        break;
        case CREDENTIAL_RECOGNITION_TYPE.FACIAL:
            translatedValue = translator.byKey(TranslationKeys.facial);
        break;
        case CREDENTIAL_RECOGNITION_TYPE.FINGER:
            translatedValue = translator.byKey(TranslationKeys.finger);
        break;
    }
    return translatedValue
}

export const translateCredentialStatus= (keyToTranslate: string, translator: Translator) => {
    let translatedValue = keyToTranslate;
    switch(keyToTranslate) {
        case CREDENTIAL_STATES.SUSPEND:
            translatedValue = translator.byKey(TranslationKeys.suspend);
            break;
        case CREDENTIAL_STATES.SUSPEND_WITH_ALARM:
            translatedValue = translator.byKey(TranslationKeys.suspendWithAlarm);
            break;
        case CREDENTIAL_STATES.ACCESS_OVERRIDE:
            translatedValue = translator.byKey(TranslationKeys.accessOverride);
            break;
        case CREDENTIAL_STATES.SCHEDULED_TOUR:
            translatedValue = translator.byKey(TranslationKeys.scheduledTour);
            break;
        case CREDENTIAL_STATES.NORMAL:
            translatedValue = translator.byKey(TranslationKeys.normal);
            break;
        case CREDENTIAL_STATES.SPECIAL_EV1:
            translatedValue = translator.byKey(TranslationKeys.specialEvent1);
            break;
        case CREDENTIAL_STATES.SPECIAL_EV2:
            translatedValue = translator.byKey(TranslationKeys.specialEvent2);
            break;
        case CREDENTIAL_STATES.OSS_SO:
            translatedValue = translator.byKey(TranslationKeys.ossEnabled);
            break;
    }
    return translatedValue;
}

export const translateCredentialDesc = (keyToTranslate: string, translator: Translator) => {
    let translatedValue = keyToTranslate;
    switch(keyToTranslate) {
        case Fingers.LEFT_INDEX:
            translatedValue = translator.byKey(TranslationKeys.leftIndex);
        break;
        case Fingers.LEFT_LITTLE:
            translatedValue = translator.byKey(TranslationKeys.leftLittle);
        break;
        case Fingers.LEFT_MIDDLE:
            translatedValue = translator.byKey(TranslationKeys.leftMiddle);
        break;
        case Fingers.LEFT_RING:
            translatedValue = translator.byKey(TranslationKeys.leftRing);
        break;
        case Fingers.LEFT_THUMB:
            translatedValue = translator.byKey(TranslationKeys.leftThumb);
        break;
        case Fingers.RIGHT_INDEX:
            translatedValue = translator.byKey(TranslationKeys.rightIndex);
        break;
        case Fingers.RIGHT_LITTLE:
            translatedValue = translator.byKey(TranslationKeys.rightLittle);
        break;
        case Fingers.RIGHT_MIDDLE:
            translatedValue = translator.byKey(TranslationKeys.rightMiddle);
        break;
        case Fingers.RIGHT_RING:
            translatedValue = translator.byKey(TranslationKeys.rightMiddle);
        break;
        case Fingers.RIGHT_THUMB:
            translatedValue = translator.byKey(TranslationKeys.rightThumb);
        break;
        case Fingers.UNKNOWN:
            translatedValue = translator.byKey(TranslationKeys.unknown);
        break;
        case CREDENTIAL_RECOGNITION_TYPE.STID_MOBILE:
            translatedValue = translator.byKey(TranslationKeys.stidMobile);
        break;
        case CREDENTIAL_RECOGNITION_TYPE.HID_MOBILE_ACCESS:
            translatedValue = translator.byKey(TranslationKeys.hidMobileAccess);
        break;
    }

    return translatedValue;
}
